import { z } from "zod";
import { multilineContentSchema } from "../content/schema.js";
import { capitalize } from "../helpers/utilities.js";
import { CustomFieldResourceType } from "./customField.js";
import {
  isLlmExtractedPresetOutcome,
  outcomeDefinitionSchema
} from "./outcome.js";
import { getCriteriaOutcomeImpactLabel } from "./teammatePerformanceSummary.js";
const PlaybookBranch = {
  /** Points to the currently live/published revision. */
  Live: "live",
  /** Points to the most recent draft revision. (We may have per-temmate branches in the future.) */
  Draft: "draft"
};
const allPlaybookBranches = [
  PlaybookBranch.Live,
  PlaybookBranch.Draft
];
const playbookBranchSchema = z.enum(allPlaybookBranches);
const CustomerInteractionSplitting = {
  Never: "never",
  AlwaysSplit: "always_split"
};
const customerInteractionSplittingSchema = z.enum([
  CustomerInteractionSplitting.Never,
  CustomerInteractionSplitting.AlwaysSplit
]);
const createdAtBasisSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("first_activity") }),
  z.object({
    type: z.literal("custom_field"),
    custom_field_definition_id: z.uuid()
  })
]);
const FIRST_ACTIVITY_CREATED_AT_BASIS = {
  type: "first_activity"
};
const CREATED_AT_BASIS_ELIGIBLE_RESOURCE_TYPES = [
  CustomFieldResourceType.GRADING,
  CustomFieldResourceType.ACCOUNT,
  CustomFieldResourceType.CONTACT
];
function getCustomerInteractionSplitting(playbook) {
  return playbook?.settings?.customer_interaction_splitting ?? CustomerInteractionSplitting.Never;
}
function getCreatedAtBasis(playbook) {
  return playbook?.created_at_basis ?? FIRST_ACTIVITY_CREATED_AT_BASIS;
}
const FIRST_ACTIVITY_DATE_LABEL = "First call date";
function getCreatedAtBasisLabel(basis, customFieldDisplayNamesById) {
  if (basis.type === "first_activity") {
    return FIRST_ACTIVITY_DATE_LABEL;
  }
  return customFieldDisplayNamesById.get(basis.custom_field_definition_id) ?? FIRST_ACTIVITY_DATE_LABEL;
}
function toStoredCreatedAtBasis(basis) {
  return basis.type === "first_activity" ? null : basis;
}
const playbookViewSchema = z.enum(["calls", "team"]);
const EMPTY_PLAYBOOK_VIEW_DEFAULTS = Object.freeze({});
function getPlaybookViewDefaults(playbook) {
  return playbook?.view_defaults ?? EMPTY_PLAYBOOK_VIEW_DEFAULTS;
}
function getPlaybookViewFilterDefaults(playbook, view) {
  return getPlaybookViewDefaults(playbook)[view];
}
function getPlaybookKnowledgeBaseIds(playbook) {
  return playbook.settings?.knowledge_base?.ids ?? [];
}
function checkPlaybookHasKnowledgeBaseAdherence(playbook) {
  const knowledgeBaseIds = getPlaybookKnowledgeBaseIds(playbook);
  return knowledgeBaseIds.length > 0;
}
function getOutcomeDefinitionFromPlaybook(playbook) {
  return playbook?.settings?.outcomes?.at(0);
}
function getEvaluableOutcomeDefinitionsFromPlaybook(playbook) {
  return (playbook?.settings?.outcomes ?? []).filter(
    isLlmExtractedPresetOutcome
  );
}
function getSummaryTemplateBodyFromPlaybook(playbook) {
  return playbook?.settings?.summary_template?.body ?? null;
}
const UNSPLIT_SPLIT_KEY = "00000000-0000-0000-0000-000000000000";
function getPlaybookSplitKey(playbook, recordingId) {
  const splitting = getCustomerInteractionSplitting(playbook);
  if (splitting === CustomerInteractionSplitting.AlwaysSplit && recordingId) {
    return recordingId;
  }
  return UNSPLIT_SPLIT_KEY;
}
const adminApiPlaybookFormatSchema = z.enum(["minimal"]);
function serializePlaybookForAdminApi(playbookDb, playbookRevisionDb) {
  return {
    company_id: playbookDb.company_id,
    id: playbookDb.id,
    revision_id: playbookRevisionDb?.id ?? playbookDb.live_revision_id ?? null,
    revision_created_at: playbookRevisionDb?.created_at ?? null,
    is_live: !playbookRevisionDb || playbookRevisionDb.id === playbookDb.live_revision_id,
    name: playbookRevisionDb?.name ?? playbookDb.name,
    assessment_tree: playbookRevisionDb?.tree ?? playbookDb.assessment_tree,
    settings: playbookRevisionDb?.settings ?? playbookDb.settings ?? {},
    created_at_basis: getCreatedAtBasis(playbookDb),
    view_defaults: getPlaybookViewDefaults(playbookDb)
  };
}
function serializePlaybookPublishedVersionForAdminApi(revision, isCurrentLiveRevision) {
  const publishedAt = revision.metadata.published_at;
  if (!publishedAt) {
    return null;
  }
  return {
    id: revision.id,
    published_at: publishedAt,
    name: revision.name,
    is_current: isCurrentLiveRevision
  };
}
const playbookIdSchema = z.uuid().brand();
function asPlaybookId(id) {
  return playbookIdSchema.parse(id);
}
const playbookSelectionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("all") }),
  z.object({ type: z.literal("all_playbooks") }),
  z.object({ type: z.literal("all_voice_agents") }),
  z.object({
    type: z.literal("playbooks"),
    ids: z.array(playbookIdSchema).min(1).refine((ids) => new Set(ids).size === ids.length, {
      message: "Playbook ids must be unique"
    })
  })
]);
function checkPlaybookSelected(selection, playbookId, isVoiceAgentPlaybook) {
  switch (selection.type) {
    case "all":
      return true;
    case "all_playbooks":
      return !isVoiceAgentPlaybook;
    case "all_voice_agents":
      return isVoiceAgentPlaybook;
    case "playbooks":
      return selection.ids.includes(playbookId);
  }
}
const playbookNodeIdSchema = z.uuid();
const CriterionCoachingPriority = {
  Critical: "critical",
  Normal: "normal"
};
function isPlaybookSectionNode(node) {
  return node.type === "section";
}
function isPlaybookCriterionNode(node) {
  return node.type === "criteria";
}
const criterionCoachingPrioritySchema = z.enum([
  CriterionCoachingPriority.Critical,
  CriterionCoachingPriority.Normal
]);
const playbookNodeSchema = z.lazy(
  () => z.discriminatedUnion("type", [
    z.object({
      type: z.literal("section"),
      id: z.uuid(),
      name: z.string(),
      description: z.string().optional(),
      nodes: z.array(playbookNodeSchema)
    }),
    z.object({
      type: z.literal("criteria"),
      id: z.uuid(),
      name: z.string(),
      description: z.string().optional(),
      grading_context: z.string().optional(),
      grading_value_type: z.enum(["yes_no", "1_5_score"]),
      coaching_priority: criterionCoachingPrioritySchema.optional()
    })
  ])
);
const playbookTreeSchema = z.object({
  nodes: z.array(playbookNodeSchema)
});
function collectPlaybookSections(subtree) {
  return subtree.nodes.flatMap((node) => {
    if (isPlaybookSectionNode(node)) {
      return [node, ...collectPlaybookSections(node)];
    } else {
      return [];
    }
  });
}
function collectPlaybookCriteria(subtree) {
  return subtree.nodes.flatMap((node) => {
    if (isPlaybookSectionNode(node)) {
      return collectPlaybookCriteria(node);
    } else {
      return [node];
    }
  });
}
function collectAllPlaybookNodes(subtree) {
  return flattenPlaybookTreeWithDepth(subtree).map((entry) => entry.node);
}
function flattenPlaybookTreeWithDepth(tree) {
  function flattenNodes(nodes, depth) {
    return nodes.flatMap(
      (node) => isPlaybookSectionNode(node) ? [{ node, depth }, ...flattenNodes(node.nodes, depth + 1)] : [{ node, depth }]
    );
  }
  return flattenNodes(tree.nodes, 0);
}
function getPlaybookNode(nodeId, subtree) {
  const allNodes = collectAllPlaybookNodes(subtree);
  return allNodes.find((node) => node.id === nodeId);
}
function findNodeWithPath(tree, nodeId) {
  function searchNodes(nodes, currentSectionsStack) {
    for (const node of nodes) {
      if (isPlaybookCriterionNode(node)) {
        if (node.id === nodeId) {
          return { node, sectionsStack: currentSectionsStack };
        }
      } else if (isPlaybookSectionNode(node)) {
        if (node.id === nodeId) {
          return { node, sectionsStack: currentSectionsStack };
        }
        const newSectionsStack = [...currentSectionsStack, node];
        const result = searchNodes(node.nodes, newSectionsStack);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }
  return searchNodes(tree.nodes, []);
}
function buildScopedGradingNodeIds(tree, nodeId) {
  const nodeWithPath = findNodeWithPath(tree, nodeId);
  if (!nodeWithPath) {
    return null;
  }
  return [
    ...nodeWithPath.sectionsStack.map((section) => section.id),
    nodeWithPath.node.id
  ];
}
const playbookCreatePayloadSchema = z.object({
  name: z.string().nonempty(),
  assessment_tree: playbookTreeSchema,
  knowledge_base_ids: z.array(z.uuid()),
  grading_data_extraction_field_ids: z.array(z.uuid()).optional(),
  outcomes: z.array(outcomeDefinitionSchema).optional(),
  /**
   * Body of the grading summary template. When provided, stored in the
   * playbook's `settings.summary_template.body`. Sending `null`
   * clears it.
   */
  summary_template_body: multilineContentSchema.nullable().optional(),
  /**
   * Customer-interaction-splitting strategy. Defaults to "never" when omitted.
   */
  customer_interaction_splitting: customerInteractionSplittingSchema.optional()
});
const playbookUpdatePayloadSchema = z.object({
  /** If provided, the parent revision to use for conflict resolution and change history tracking. */
  parent_revision_id: z.string(),
  name: z.string().nonempty(),
  assessment_tree: playbookTreeSchema,
  grading_data_extraction_field_ids: z.array(z.uuid()),
  knowledge_base_ids: z.array(z.uuid()),
  outcomes: z.array(outcomeDefinitionSchema).optional(),
  /**
   * Body of the grading summary template. When provided, updates the
   * playbook's `settings.summary_template.body`. Sending `null`
   * clears it.
   */
  summary_template_body: multilineContentSchema.nullable(),
  /**
   * Customer-interaction-splitting strategy. When provided, updates the
   * playbook's `settings.customer_interaction_splitting`.
   */
  customer_interaction_splitting: customerInteractionSplittingSchema
}).partial();
const playbookPublishTargetSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("node"), node_id: playbookNodeIdSchema }),
  z.object({ type: z.literal("outcome") }),
  z.object({ type: z.literal("summary_template") })
]);
const playbookPublishChangesPayloadSchema = z.object({
  targets: z.array(playbookPublishTargetSchema).min(1),
  draft: z.object({
    assessment_tree: playbookTreeSchema,
    outcome: outcomeDefinitionSchema.nullable(),
    summary_template_body: multilineContentSchema.nullable()
  })
}).superRefine(({ targets }, context) => {
  const seenTargets = /* @__PURE__ */ new Set();
  targets.forEach((target, index) => {
    const key = target.type === "node" ? `node:${target.node_id}` : target.type;
    if (seenTargets.has(key)) {
      context.addIssue({
        code: "custom",
        message: `Duplicate publish target: ${key}`,
        path: ["targets", index]
      });
    }
    seenTargets.add(key);
  });
});
function generateYamlTreeFromPlaybook(nodes, criterionStyle, options) {
  const promptStrings = [];
  function traverseNodes(nodes2, depth = 0) {
    const indent = "  ".repeat(depth);
    for (const node of nodes2) {
      promptStrings.push(
        ...getNodeAsYamlStrings(node, indent, criterionStyle, options)
      );
      if (isPlaybookSectionNode(node)) {
        const hasNoNodes = node.nodes.length === 0;
        if (hasNoNodes) {
          continue;
        }
        traverseNodes(node.nodes, depth + 1);
      }
      promptStrings.push("");
    }
  }
  traverseNodes(nodes);
  return promptStrings.join("\n");
}
function getNodeAsYamlStrings(node, indent, criterionStyle, options) {
  const promptStrings = [];
  if (isPlaybookSectionNode(node)) {
    promptStrings.push(`${indent}- Section: ${node.name}`);
    if (!options?.excludeIds) {
      promptStrings.push(`${indent}  ID: ${node.id}`);
    }
    if (node.description) {
      promptStrings.push(`${indent}  Description: ${node.description}`);
    }
    return promptStrings;
  } else if (isPlaybookCriterionNode(node)) {
    promptStrings.push(`${indent}- Criteria: ${node.name}`);
    if (!options?.excludeIds) {
      promptStrings.push(`${indent}  ID: ${node.id}`);
    }
    promptStrings.push(
      `${indent}  Grading value type: ${node.grading_value_type}`
    );
    if (criterionStyle === "with_grading_context" && node.grading_context) {
      promptStrings.push(`${indent}  Grading Context: ${node.grading_context}`);
    }
    if (options?.outcomeImpact) {
      const correlation = options.outcomeImpact.correlationsByCriteriaId.get(
        node.id
      );
      if (correlation) {
        const impactLabel = getCriteriaOutcomeImpactLabel(
          correlation.metrics.auc
        );
        promptStrings.push(
          `${indent}  Impact on ${options.outcomeImpact.outcomeRateLabel}: ${impactLabel}`
        );
      }
    }
  }
  return promptStrings;
}
function areCriteriaEqual(a, b) {
  return a.name === b.name && a.description === b.description && a.grading_context === b.grading_context && a.grading_value_type === b.grading_value_type && (a.coaching_priority ?? CriterionCoachingPriority.Normal) === (b.coaching_priority ?? CriterionCoachingPriority.Normal);
}
function areSectionsEqual(a, b) {
  return a.name === b.name && a.description === b.description;
}
function computePlaybookChanges(initial, current) {
  if (!initial || !current) {
    return null;
  }
  const initialCriteria = collectPlaybookCriteria(initial);
  const currentCriteria = collectPlaybookCriteria(current);
  const initialSections = collectPlaybookSections(initial);
  const currentSections = collectPlaybookSections(current);
  const initialCriteriaMap = new Map(initialCriteria.map((c) => [c.id, c]));
  const currentCriteriaMap = new Map(currentCriteria.map((c) => [c.id, c]));
  const initialSectionMap = new Map(initialSections.map((s) => [s.id, s]));
  const currentSectionMap = new Map(currentSections.map((s) => [s.id, s]));
  let criteriaAdded = 0;
  let criteriaModified = 0;
  let criteriaRemoved = 0;
  for (const criteria of currentCriteria) {
    const initialVersion = initialCriteriaMap.get(criteria.id);
    if (!initialVersion) {
      criteriaAdded++;
    } else if (!areCriteriaEqual(initialVersion, criteria)) {
      criteriaModified++;
    }
  }
  for (const criteria of initialCriteria) {
    if (!currentCriteriaMap.has(criteria.id)) {
      criteriaRemoved++;
    }
  }
  let sectionsAdded = 0;
  let sectionsModified = 0;
  let sectionsRemoved = 0;
  for (const section of currentSections) {
    const initialVersion = initialSectionMap.get(section.id);
    if (!initialVersion) {
      sectionsAdded++;
    } else if (!areSectionsEqual(initialVersion, section)) {
      sectionsModified++;
    }
  }
  for (const section of initialSections) {
    if (!currentSectionMap.has(section.id)) {
      sectionsRemoved++;
    }
  }
  return {
    criteria: {
      added: criteriaAdded,
      modified: criteriaModified,
      removed: criteriaRemoved
    },
    sections: {
      added: sectionsAdded,
      modified: sectionsModified,
      removed: sectionsRemoved
    }
  };
}
function formatEntityChanges(counts, singular, plural) {
  const changes = [];
  if (counts.added > 0) {
    changes.push(
      `added ${counts.added} ${counts.added === 1 ? singular : plural}`
    );
  }
  if (counts.modified > 0) {
    changes.push(
      `modified ${counts.modified} ${counts.modified === 1 ? singular : plural}`
    );
  }
  if (counts.removed > 0) {
    changes.push(
      `removed ${counts.removed} ${counts.removed === 1 ? singular : plural}`
    );
  }
  return changes;
}
function formatPlaybookChanges(changes) {
  const criteriaChanges = formatEntityChanges(
    changes.criteria,
    "criterion",
    "criteria"
  );
  const sectionChanges = formatEntityChanges(
    changes.sections,
    "section",
    "sections"
  );
  return [
    criteriaChanges.length > 0 ? capitalize(criteriaChanges.join(", ")) : null,
    sectionChanges.length > 0 ? capitalize(sectionChanges.join(", ")) : null
  ].filter((part) => part !== null);
}
function computeNodeChangeStatuses(initial, current) {
  const changeMap = /* @__PURE__ */ new Map();
  if (!initial || !current) {
    return changeMap;
  }
  const initialCriteria = collectPlaybookCriteria(initial);
  const currentCriteria = collectPlaybookCriteria(current);
  const initialSections = collectPlaybookSections(initial);
  const currentSections = collectPlaybookSections(current);
  const initialCriteriaMap = new Map(initialCriteria.map((c) => [c.id, c]));
  const initialSectionMap = new Map(initialSections.map((s) => [s.id, s]));
  for (const criteria of currentCriteria) {
    const initialVersion = initialCriteriaMap.get(criteria.id);
    if (!initialVersion) {
      changeMap.set(criteria.id, "new");
    } else if (!areCriteriaEqual(initialVersion, criteria)) {
      changeMap.set(criteria.id, "edited");
    }
  }
  for (const section of currentSections) {
    const initialVersion = initialSectionMap.get(section.id);
    if (!initialVersion) {
      changeMap.set(section.id, "new");
    } else if (!areSectionsEqual(initialVersion, section)) {
      changeMap.set(section.id, "edited");
    }
  }
  return changeMap;
}
export {
  CREATED_AT_BASIS_ELIGIBLE_RESOURCE_TYPES,
  CriterionCoachingPriority,
  CustomerInteractionSplitting,
  FIRST_ACTIVITY_CREATED_AT_BASIS,
  FIRST_ACTIVITY_DATE_LABEL,
  PlaybookBranch,
  UNSPLIT_SPLIT_KEY,
  adminApiPlaybookFormatSchema,
  areCriteriaEqual,
  areSectionsEqual,
  asPlaybookId,
  buildScopedGradingNodeIds,
  checkPlaybookHasKnowledgeBaseAdherence,
  checkPlaybookSelected,
  collectAllPlaybookNodes,
  collectPlaybookCriteria,
  collectPlaybookSections,
  computeNodeChangeStatuses,
  computePlaybookChanges,
  createdAtBasisSchema,
  criterionCoachingPrioritySchema,
  customerInteractionSplittingSchema,
  findNodeWithPath,
  flattenPlaybookTreeWithDepth,
  formatPlaybookChanges,
  generateYamlTreeFromPlaybook,
  getCreatedAtBasis,
  getCreatedAtBasisLabel,
  getCustomerInteractionSplitting,
  getEvaluableOutcomeDefinitionsFromPlaybook,
  getNodeAsYamlStrings,
  getOutcomeDefinitionFromPlaybook,
  getPlaybookKnowledgeBaseIds,
  getPlaybookNode,
  getPlaybookSplitKey,
  getPlaybookViewDefaults,
  getPlaybookViewFilterDefaults,
  getSummaryTemplateBodyFromPlaybook,
  isPlaybookCriterionNode,
  isPlaybookSectionNode,
  playbookBranchSchema,
  playbookCreatePayloadSchema,
  playbookIdSchema,
  playbookNodeIdSchema,
  playbookPublishChangesPayloadSchema,
  playbookPublishTargetSchema,
  playbookSelectionSchema,
  playbookTreeSchema,
  playbookUpdatePayloadSchema,
  playbookViewSchema,
  serializePlaybookForAdminApi,
  serializePlaybookPublishedVersionForAdminApi,
  toStoredCreatedAtBasis
};
//# sourceMappingURL=playbook.js.map
